import {
  Button,
  Center,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRegionInterviewConfig, IStudentRow } from 'types';
import { ApplicationState } from '../../../../store';
import { getUniRegionInterviewConfig } from '../../../../store/config/actions';
import { IEventType } from '../../../../store/event_types/types';
import { bulkActionGetInstitutionType } from '../../../../utilities/bulkActions';
import { getActiveFiltersFromParams } from '@/components/organisms/Filters/helpers';
import { useTranslation } from "react-i18next";

export interface CheckRadioGroupInfo {
  id: string;
  label: string | React.ReactElement;
  value: string;
  hint?: string;
  checked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  key?: string;
}

export interface GroupedCheckRadioGroupInfo {
  label: string | React.ReactElement;
  options: CheckRadioGroupInfo[];
}

export interface IRequestInterviewModalV2Data {
  clickAction: (eventType?: IEventType, regionInterviewConfig?: IRegionInterviewConfig) => void;
  descriptionText?: string;
  modalTitle: string;
  confirmationButtonText: string;
  uni: string;
  pathway?: string;
}

export interface IProps {
  id?: string;
  modalTitle: string;
  descriptionText?: string;
  confirmationButtonText: string;
  uni: string;
  pathway?: string;
  selectedStudents: IStudentRow[];
  isCancelling?: boolean;
  onAddRequest: (eventType?: IEventType, regionInterviewConfig?: IRegionInterviewConfig) => void;
  onClose: () => void;
}

const RequestNewInterviewV2 = (props: IProps) => {
    const { t } = useTranslation('dialogs');
  const {
    id = 'request-new-interview',
    modalTitle,
    confirmationButtonText,
    uni,
    pathway,
    selectedStudents,
    isCancelling,
    onAddRequest,
    onClose,
  } = props;

  const dispatch = useDispatch();
  useEffect(() => {
    if (uni.length) {
      dispatch(getUniRegionInterviewConfig(uni, pathway));
    }
  }, [dispatch, uni, pathway]);

  const isCalendlyEnabled = useSelector(({ config }: ApplicationState) => !!config.CalendlyConfig?.isCalendlyEnabled);
  const isRecordedInterviewEnabled = useSelector(
    ({ config }: ApplicationState) => !!config?.recordedInterview?.enabled,
  );
  const regionInterviewConfig = useSelector(({ config }: ApplicationState) => config.regionInterviewConfig);
  const eventTypes = useSelector(({ eventTypes }: ApplicationState) => eventTypes);

  const [selectedValue, setSelectedValue] = useState<string>('');

  const [isLoading, setIsLoading] = useState(true);

  const allSelected = useSelector(({ dashboard }: ApplicationState) => dashboard.allSelected);
  const isPathway = useSelector(({ config }: ApplicationState) => !!config.isPathway);

  const isOnlyOneUni = useCallback(() => {
    const isSelectedStudentsSameInstitution = selectedStudents.every(
      (student: IStudentRow) => student.uni === selectedStudents[0].uni,
    );
    if (!allSelected && isSelectedStudentsSameInstitution) {
      return true;
    }
    if (allSelected) {
      const studentInstitutionType = bulkActionGetInstitutionType(allSelected, selectedStudents, isPathway);
      if (studentInstitutionType === 'uni') {
        return true;
      }
      if (studentInstitutionType === 'pathway') {
        const activeFilters = getActiveFiltersFromParams(new URLSearchParams(window.location.search));
        return activeFilters?.filter_university?.length === 1;
      }
    }
    return false;
  }, [allSelected, selectedStudents, isPathway]);

  const groupedItems: GroupedCheckRadioGroupInfo[] | null = useMemo((): GroupedCheckRadioGroupInfo[] | null => {
    setIsLoading(true);
    if (regionInterviewConfig && eventTypes) {
      const recordedInterviews = (isCancelling || isOnlyOneUni()) &&
        isRecordedInterviewEnabled &&
        regionInterviewConfig.length && {
          label: 'Recorded video interviews',
          options: regionInterviewConfig.map((interview, index) => ({
            id: `recorded-${id}-${index}`,
            label: interview.interviewName || '',
            value: interview.interviewId || '',
          })),
        };
      const calendlyInterviews =
        isCalendlyEnabled && eventTypes?.length
          ? {
              label: 'Interview bookings',
              options: eventTypes
                .filter(item => item.id !== null)
                .map((event, index) => ({
                  id: `calendly-${id}-${index}`,
                  label: event.name ?? '',
                  value: event.id!,
                  checked: false,
                })),
            }
          : null;
      setIsLoading(false);

      return recordedInterviews && calendlyInterviews
        ? [recordedInterviews, calendlyInterviews]
        : recordedInterviews
          ? [recordedInterviews]
          : calendlyInterviews
            ? [calendlyInterviews]
            : null;
    }

    return null;
  }, [
    regionInterviewConfig,
    eventTypes,
    isCalendlyEnabled,
    isRecordedInterviewEnabled,
    id,
    isCancelling,
    isOnlyOneUni,
  ]);

  useEffect(() => {
    if (groupedItems && groupedItems[0]?.options.length) setSelectedValue(groupedItems[0].options[0].value);
  }, [groupedItems]);

  const onChangeRegionInterviewConfig = (event: ChangeEvent<HTMLInputElement>): void => {
    setSelectedValue(event.currentTarget.value);
  };

  const onInvite = () => {
    const eventTypeObj = eventTypes.find(event => event.id === selectedValue)!;
    if (eventTypeObj) {
      onAddRequest(eventTypeObj);
      onClose();
    }

    const recordedInterview = regionInterviewConfig.find(interview => interview.interviewId === selectedValue);
    if (recordedInterview) {
      onAddRequest(undefined, recordedInterview);
      onClose();
    }
  };

  const renderGroupItems = () => (
    <RadioGroup name="grouped-radio" defaultValue={selectedValue}>
      {groupedItems?.map((groupedItem, groupedItemIndex: number) => {
        return (
          <React.Fragment key={`grouped-item-${groupedItemIndex}`}>
            {groupedItem.label && (
              <Flex mb="2" alignItems="center" justifyContent="space-between">
                <Heading as="h3" fontSize="md">
                  {groupedItem.label}
                </Heading>
              </Flex>
            )}

            <Stack spacing="0.5" mb="4">
              {groupedItem.options.map(({ id, value, label }, index: number) => (
                <Radio
                  key={`grouped-item-${id}-${index}`}
                  id={id}
                  value={value}
                  onChange={onChangeRegionInterviewConfig}
                  variant="ghost"
                  data-selector={id}
                >
                  {label}
                </Radio>
              ))}
            </Stack>
          </React.Fragment>
        );
      })}
    </RadioGroup>
  );

  return (
    <Modal onClose={onClose} isOpen={true} size="md" scrollBehavior="inside">
      <ModalOverlay />

      {isLoading && (
        <Center>
          <Spinner />
        </Center>
      )}
      <ModalContent>
        <ModalHeader display="flex">{modalTitle}</ModalHeader>

        <ModalCloseButton />

        <ModalBody key={`selected-${selectedValue}`}>{renderGroupItems()}</ModalBody>
        {!isCancelling && !isLoading && (
          <Text color="muted" fontSize="sm" py={2} px={6}>
            {t('dialogs:thisActionWillOnlyInviteStudents')}
                                </Text>
        )}
        <ModalFooter gap="3">
          <Button size="md" onClick={onClose}>
            {t('common:cancel')}
                                </Button>

          <Button size="md" variant="solid" onClick={onInvite}>
            {confirmationButtonText}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RequestNewInterviewV2;
